import type { GraphQLClient } from "graphql-request";
import {
  EventTimelineAdvancedWidget,
  type LiveMonitoringData,
  type MonitoredEvent,
} from "@nexus/widgets";
import { EVENT_TIMELINE_QUERY, normalizeEventTimeline } from "./eventTimelineData";
import { fetchEventMonitoringTrends, EVENT_TREND_GROUPS } from "@/hooks/live/useEventTrendsData";

export async function fetchLiveEventMonitoring(
  params: Record<string, unknown>,
  client: GraphQLClient,
): Promise<LiveMonitoringData> {
  const timeline = normalizeEventTimeline(await client.request(EVENT_TIMELINE_QUERY, params));
  const from = String(params.from_ts);
  const to = String(params.to_ts);
  const now = Date.now();
  const events: MonitoredEvent[] = timeline.events.map((event) => ({
    ...event,
    segments: [],
    performanceState: "scheduled",
  }));
  let next = 0;
  // Bounded fan-out, existing authorized tenant scope, no invented API fields.
  async function worker() {
    while (next < events.length) {
      const index = next++;
      const event = events[index];
      if (EventTimelineAdvancedWidget.eventTime(event.event_start) > now) continue;
      try {
        const channels = await fetchEventMonitoringTrends(client, {
          eventId: event.event_id,
          from,
          to,
          tenantIds: (params.tenant_ids as number[] | undefined) ?? [],
        });
        event.segments = channels.flatMap((channel) =>
          channel.groups.map((group) =>
            EventTimelineAdvancedWidget.summarizeSegment(group.evolution, {
              protectedEventId: channel.protectedEventId,
              tenant: channel.tenantLabel,
              channel: channel.channel,
              family:
                EVENT_TREND_GROUPS.find((item) => item.id === group.groupId)?.label ??
                group.groupId,
            }),
          ),
        );
        event.performanceState = event.segments.length ? "ready" : "unavailable";
      } catch {
        event.performanceState = "unavailable";
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(3, events.length) }, worker));
  return { events, from, to, fetchedAt: new Date().toISOString() };
}
